/**
 * Per-business-type UI configuration.
 *
 * This is deliberately NOT the same mechanism as `module_access`, which the
 * server owns. The two answer different questions:
 *
 *   module_access  — "your plan doesn't include this"  → item stays visible,
 *                    carries a crown, and clicking it opens the upgrade wall.
 *                    That visibility is the upsell, so it must not be hidden.
 *
 *   this file      — "this isn't part of your business" → item is hidden
 *                    outright. A gym has no Menus; showing one and then
 *                    offering to sell it would be nonsense.
 *
 * Keyed by `business_types.slug`. Anything not listed here — including a slug
 * added to the DB but not yet added below — falls back to DEFAULT_BUSINESS_TYPE,
 * which hides nothing. Failing open matters: a merchant losing navigation is a
 * far worse outcome than one seeing a tab they don't need.
 */

/** Slug used when the restaurant's type is null, unknown, or unmapped. */
export const DEFAULT_BUSINESS_TYPE = 'restaurant-cafe';

/**
 * `hidden` holds module names, matching the `module` key on nav items in
 * menu-items/pages.jsx and the `modules` map returned by /me.
 *
 * `terms` is domain vocabulary. A salon takes an Appointment, not a Booking.
 *
 * `ui` gates fields and sections that exist on shared screens but only make
 * sense for some trades — the flag names describe the feature, not the type,
 * so a second business type can opt in without renaming anything.
 */
export const BUSINESS_TYPES = {
  'restaurant-cafe': {
    label: 'Restaurant / Cafe',
    hidden: [],
    navTitles: {},
    terms: { booking: 'Booking', bookings: 'Bookings', customer: 'Guest', customers: 'Guests', resource: 'Table', resources: 'Tables', menu: 'Menu', menus: 'Menus' },
    ui: { coverCharge: true, tableLayout: true, serviceDuration: false, servicePeriodTimes: true, servicePeriodScheduling: true }
  },

  'salon-barber-shop': {
    label: 'Salon / Barber Shop',
    // `menus` stays visible: a salon's services are menu records, shown under
    // the "Service" label via navTitles below. What it loses is the food
    // apparatus around them and the ticketing side.
    //
    // `pos` goes too — it exists to set daily stock, and a haircut doesn't run
    // out. Mirrored by no_pos_inventory in the backend's config/business_types.php,
    // which stops those never-configured zero rows from blocking bookings.
    hidden: ['table_layout', 'table_orders', 'kitchen', 'pos', 'events', 'tickets', 'public_pass'],
    navTitles: {
      menus: 'Service',
      // Custom Setup holds two tabs, and a salon has no use for scheduled
      // service periods (its groupings are Men / Women / Kids, not time
      // windows). With only Holiday left, the old label describes nothing.
      custom_setup: 'Holiday'
    },
    terms: {
      booking: 'Appointment',
      bookings: 'Appointments',
      customer: 'Client',
      customers: 'Clients',
      resource: 'Chair',
      resources: 'Chairs',
      menu: 'Service',
      menus: 'Services',
      servicePeriod: 'Service For',
      capacity: 'Number of Chairs',
      capacityPlaceholder: 'e.g. 6'
    },
    ui: {
      coverCharge: false,
      tableLayout: false,
      serviceDuration: true,
      // Service periods are plain named groupings here — no start/end times, so
      // the Custom Setup scheduling tab is dropped and slots come off business
      // hours instead of a period's own window.
      servicePeriodTimes: false,
      servicePeriodScheduling: false,
      bufferSeats: false,
      takeawayOption: false,
      preOrder: false,
      selectServiceUpfront: true,
      servicePeriodIcons: false,
      tableAssignment: false
    }
  },

  // Mirrors salon — grooming is booked the same way, by service rather than by
  // time window — with one difference: events and ticketing stay, so adoption
  // days and workshops can be sold.
  'pet-shop': {
    label: 'Pet Salon / Pet Shop',
    hidden: ['table_layout', 'table_orders', 'kitchen', 'pos'],
    navTitles: {
      menus: 'Service',
      custom_setup: 'Holiday'
    },
    terms: {
      booking: 'Appointment',
      bookings: 'Appointments',
      customer: 'Client',
      customers: 'Clients',
      resource: 'Station',
      resources: 'Stations',
      menu: 'Service',
      menus: 'Services',
      servicePeriod: 'Service For',
      capacity: 'Number of Stations',
      capacityPlaceholder: 'e.g. 4'
    },
    ui: {
      coverCharge: false,
      tableLayout: false,
      serviceDuration: true,
      servicePeriodTimes: false,
      servicePeriodScheduling: false,
      bufferSeats: false,
      takeawayOption: false,
      preOrder: false,
      selectServiceUpfront: true,
      servicePeriodIcons: false,
      tableAssignment: false
    }
  },

  'medical-clinic': {
    label: 'Medical Clinic',
    hidden: ['menus', 'table_layout', 'table_orders', 'kitchen', 'pos', 'gift_cards', 'vouchers', 'events', 'tickets', 'public_pass'],
    terms: { booking: 'Appointment', bookings: 'Appointments', customer: 'Patient', customers: 'Patients', resource: 'Room', resources: 'Rooms' },
    ui: { coverCharge: false, tableLayout: false, serviceDuration: true }
  },

  // Sells seats to shows through Events & Tickets, not table reservations —
  // so Booking Management and Calendar come out entirely, along with the whole
  // food side. Everything a patron books is an event.
  'theater-movies': {
    label: 'Theater / Movies',
    hidden: [
      'booking_management',
      'calendar',
      'menus',
      'table_layout',
      'table_orders',
      'kitchen',
      'pos',
      'preference_amenities'
    ],
    terms: {
      booking: 'Booking',
      bookings: 'Bookings',
      customer: 'Patron',
      customers: 'Patrons',
      resource: 'Screen',
      resources: 'Screens',
      capacity: 'Seating Capacity',
      capacityPlaceholder: 'Enter seating'
    },
    ui: {
      coverCharge: false,
      tableLayout: false,
      serviceDuration: false,
      takeawayOption: false,
      preOrder: false,
      tableAssignment: false
    }
  },

  'gym-fitness': {
    label: 'Gym / Fitness',
    // Classes are sold as ticketed sessions here rather than table-style
    // reservations, so booking_management and calendar come out while
    // events/tickets stay in.
    hidden: ['booking_management', 'calendar', 'menus', 'table_layout', 'table_orders', 'kitchen'],
    terms: { booking: 'Session', bookings: 'Sessions', customer: 'Member', customers: 'Members', resource: 'Studio', resources: 'Studios' },
    ui: { coverCharge: false, tableLayout: false, serviceDuration: true }
  },

  // Picked by merchants whose trade isn't listed. Nothing is assumed about
  // them, so nothing is taken away.
  other: {
    label: 'Other',
    hidden: [],
    terms: { booking: 'Booking', bookings: 'Bookings', customer: 'Customer', customers: 'Customers', resource: 'Resource', resources: 'Resources' },
    ui: { coverCharge: false, tableLayout: false, serviceDuration: false }
  }
};

/**
 * Shape every resolved config is guaranteed to have. Entries above can be
 * sparse — only what differs needs stating — and adding a new key here can't
 * break an existing entry by omission.
 *
 * Defaults describe restaurant-shaped behaviour on purpose: an unmapped trade
 * should behave exactly as the product did before this file existed.
 */
const BASE_CONFIG = {
  label: '',
  hidden: [],
  navTitles: {},
  terms: {
    booking: 'Booking',
    bookings: 'Bookings',
    customer: 'Customer',
    customers: 'Customers',
    resource: 'Resource',
    resources: 'Resources',
    menu: 'Menu',
    menus: 'Menus',
    servicePeriod: 'Service Period',
    // Capacity is never removed — it drives guest options and slot
    // availability — so trades only rename it to whatever they actually count.
    capacity: 'Seating Capacity',
    capacityPlaceholder: 'Enter seating'
  },
  ui: {
    coverCharge: false,
    tableLayout: false,
    serviceDuration: false,
    servicePeriodTimes: true,
    servicePeriodScheduling: true,
    bufferSeats: true,

    // ── Public booking page ──────────────────────────────────────────────
    // Only meaningful where there's a counter to collect from.
    takeawayOption: true,
    // Ordering ahead assumes a kitchen; a salon has nothing to pre-order.
    preOrder: true,
    // Where what you're booking *is* the service, the customer picks it during
    // selection rather than adding items to a reservation afterwards.
    selectServiceUpfront: false,
    // The service-period icons are food-specific (breakfast / lunch / dinner,
    // falling back to a bowl). Groupings elsewhere are open-ended — "Men",
    // "Bridal", "Grooming" — so there's no icon that reliably fits, and a
    // wrong one reads worse than none.
    servicePeriodIcons: true,
    // Confirming a booking normally means picking a table off the floor plan.
    // Without a layout there's nothing to pick, so confirmation happens
    // directly instead.
    tableAssignment: true
  }
};

/**
 * Resolve a slug to its config, falling back to the default for unknown or
 * missing values. Always returns a fully-populated object, so callers can read
 * `.terms.booking` or `.ui.servicePeriodTimes` without guarding.
 */
export function getBusinessTypeConfig(slug) {
  const entry = BUSINESS_TYPES[slug] ?? BUSINESS_TYPES[DEFAULT_BUSINESS_TYPE];

  return {
    ...BASE_CONFIG,
    ...entry,
    navTitles: { ...BASE_CONFIG.navTitles, ...(entry.navTitles ?? {}) },
    terms: { ...BASE_CONFIG.terms, ...(entry.terms ?? {}) },
    ui: { ...BASE_CONFIG.ui, ...(entry.ui ?? {}) }
  };
}

/**
 * Sidebar label override for a module, or null when the default title stands.
 * Lets a salon read "Service" where a restaurant reads "Menu" without either
 * one needing its own nav definition.
 */
export function getNavTitleOverride(slug, moduleName) {
  if (!moduleName) return null;
  return getBusinessTypeConfig(slug).navTitles[moduleName] ?? null;
}

/**
 * Whether a module should be hidden from this business type.
 * Modules absent from every `hidden` list are visible to everyone.
 */
export function isModuleHiddenForBusinessType(slug, moduleName) {
  if (!moduleName) return false;
  return getBusinessTypeConfig(slug).hidden.includes(moduleName);
}
